// Pure helpers for building recipient filter payloads and merging/deduping
// manually-entered / CSV-imported / filter-resolved recipient addresses.

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email) {
    return typeof email === 'string' && EMAIL_REGEX.test(email.trim());
}

/**
 * Builds the RecipientFilterRequest payload sent to the backend estimate/validate endpoints.
 * Shape must match RecipientFilterRequest.java exactly: `criteria` is a flat list of
 * CriterionType enum names (not objects), and REGISTERED_DATE_RANGE's bounds live in the
 * top-level registeredFrom/registeredTo fields, not on the criterion itself.
 * @param {{logic: 'AND'|'OR', criteria: string[], registeredFrom?: string, registeredTo?: string}} filterState
 * @param {string[]} manualEmails
 */
export function buildFilterPayload(filterState, manualEmails = []) {
    return {
        logic: filterState?.logic || 'AND',
        criteria: filterState?.criteria || [],
        registeredFrom: filterState?.registeredFrom || null,
        registeredTo: filterState?.registeredTo || null,
        manualEmails: manualEmails.filter(isValidEmail),
    };
}

/**
 * Splits free-form manual recipient text (comma/newline separated) into a clean list.
 */
export function parseManualRecipients(text) {
    if (!text) return [];
    return text
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * Merges recipients coming from multiple sources (manual entry, CSV import, dynamic filter)
 * into a single de-duplicated, lower-cased-keyed list, tracking each address's source(s).
 */
export function mergeAndDedupeEmails(...sources) {
    const map = new Map();
    for (const source of sources) {
        const { emails = [], label } = source || {};
        for (const raw of emails) {
            const email = (raw || '').trim();
            if (!email) continue;
            const key = email.toLowerCase();
            if (!map.has(key)) {
                map.set(key, { email, valid: isValidEmail(email), sources: new Set([label]) });
            } else {
                map.get(key).sources.add(label);
            }
        }
    }
    return Array.from(map.values()).map((entry) => ({
        ...entry,
        sources: Array.from(entry.sources),
    }));
}

export function countDuplicatesAndInvalid(mergedEmails) {
    const duplicates = mergedEmails.filter((e) => e.sources.length > 1).length;
    const invalid = mergedEmails.filter((e) => !e.valid).length;
    return { duplicates, invalid, total: mergedEmails.length };
}
